import { useState } from 'react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/Field';
import { money } from '@/lib/format';
import { useVendors } from '@/features/vendors/queries';
import { useVenues } from '@/features/venues/queries';
import {
  useCreateScenario,
  useDeleteScenario,
  useProjection,
  useScenarios,
  useUpdateScenario,
} from './queries';

/**
 * "If we took this venue and these suppliers, what would it cost?"
 *
 * Deliberately ignores what is booked — that is the whole difference from the
 * table above. A scenario is for deciding, so it prices things you have not
 * committed to, which is the only interesting case.
 *
 * The projection is fetched rather than computed here: changing a supplier's
 * quote has to move every scenario naming it, and a number worked out in the
 * browser from a stale list would quietly disagree with the budget.
 */
export function Scenarios({ wedId, currency }: { wedId: string; currency: string }) {
  const { t } = useTranslation();
  const scenarios = useScenarios(wedId);
  const venues = useVenues(wedId);
  const vendors = useVendors(wedId);

  const create = useCreateScenario(wedId);
  const update = useUpdateScenario(wedId);
  const remove = useDeleteScenario(wedId);

  const items = scenarios.data?.items ?? [];
  const [selectedId, setSelectedId] = useState<string>();
  const [confirming, setConfirming] = useState(false);
  const selected = items.find((s) => s.scenarioId === selectedId) ?? items[0];
  const projection = useProjection(wedId, selected?.scenarioId);

  const [name, setName] = useState('');

  const toggleVendor = (vendorId: string) => {
    if (!selected) return;
    const has = selected.vendorIds.includes(vendorId);
    update.mutate({
      scenarioId: selected.scenarioId,
      input: {
        vendorIds: has
          ? selected.vendorIds.filter((id) => id !== vendorId)
          : [...selected.vendorIds, vendorId],
        updatedAt: selected.updatedAt,
      },
    });
  };

  const over = (projection.data?.varianceCents ?? 0) < 0;

  return (
    <section className="flex flex-col gap-4 border-t border-line pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section">{t('budget.scenarios')}</h2>
        <p className="text-caption text-leaf-soft">{t('budget.scenariosHint')}</p>
      </div>

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate({ name: name.trim() }, { onSuccess: (s) => setSelectedId(s.scenarioId) });
          setName('');
        }}
      >
        <label className="min-w-[10rem] flex-1">
          <span className="sr-only">{t('budget.scenarioName')}</span>
          <TextInput
            placeholder={t('budget.scenarioName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <Button type="submit" variant="secondary" loading={create.isPending}>
          {t('budget.addScenario')}
        </Button>
      </form>

      {items.length === 0 ? (
        <p className="text-body text-leaf-soft">{t('budget.noScenarios')}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {items.map((s) => (
              <button
                key={s.scenarioId}
                type="button"
                aria-pressed={s.scenarioId === selected?.scenarioId}
                onClick={() => setSelectedId(s.scenarioId)}
                className={`min-h-tap rounded-pill border px-3 text-caption transition-colors ${
                  s.scenarioId === selected?.scenarioId
                    ? 'border-leaf bg-leaf text-cream'
                    : 'border-line bg-paper text-leaf-soft hover:border-leaf-soft hover:text-leaf'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          {selected && (
            <div className="panel flex flex-col gap-4 p-4">
              <div className="flex flex-wrap items-baseline gap-4">
                <span className="flex flex-col">
                  <span className="eyebrow">{t('budget.projected')}</span>
                  <span className="tabular font-display text-[30px] leading-none">
                    {money(projection.data?.projectedCents ?? 0, currency)}
                  </span>
                </span>
                <span className="flex flex-col">
                  <span className="eyebrow">
                    {over ? t('budget.overPlan') : t('budget.underPlan')}
                  </span>
                  <span
                    className={`tabular font-display text-[30px] leading-none ${
                      over ? 'text-status-declinedFg' : 'text-leaf'
                    }`}
                  >
                    {money(Math.abs(projection.data?.varianceCents ?? 0), currency)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="ml-auto min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
                >
                  {t('budget.removeScenario')}
                </button>
              </div>

              {/* A supplier deleted after being ticked would otherwise just make
                  the scenario quietly cheaper. */}
              {(projection.data?.missing.length ?? 0) > 0 && (
                <p className="text-caption text-status-declinedFg" role="alert">
                  {t('budget.missingItems', { count: projection.data?.missing.length ?? 0 })}
                </p>
              )}

              <div className="flex flex-col gap-2">
                <span className="eyebrow">{t('budget.pickVenue')}</span>
                <div className="flex flex-wrap gap-2">
                  {(venues.data?.items ?? []).map((v) => (
                    <button
                      key={v.venueId}
                      type="button"
                      aria-pressed={selected.venueId === v.venueId}
                      onClick={() =>
                        update.mutate({
                          scenarioId: selected.scenarioId,
                          input: {
                            venueId: selected.venueId === v.venueId ? undefined : v.venueId,
                            updatedAt: selected.updatedAt,
                          },
                        })
                      }
                      className={`min-h-tap rounded-pill border px-3 text-caption transition-colors ${
                        selected.venueId === v.venueId
                          ? 'border-leaf bg-leaf text-cream'
                          : 'border-line bg-paper text-leaf-soft hover:border-leaf-soft'
                      }`}
                    >
                      {v.name}
                      {v.quoteCents !== undefined && (
                        <span className="tabular ml-2 opacity-70">
                          {money(v.quoteCents, v.currency ?? currency)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="eyebrow">{t('budget.pickVendors')}</span>
                <div className="flex flex-wrap gap-2">
                  {(vendors.data?.items ?? []).map((v) => (
                    <button
                      key={v.vendorId}
                      type="button"
                      aria-pressed={selected.vendorIds.includes(v.vendorId)}
                      onClick={() => toggleVendor(v.vendorId)}
                      className={`min-h-tap rounded-pill border px-3 text-caption transition-colors ${
                        selected.vendorIds.includes(v.vendorId)
                          ? 'border-leaf bg-leaf text-cream'
                          : 'border-line bg-paper text-leaf-soft hover:border-leaf-soft'
                      }`}
                    >
                      {v.name}
                      {v.quoteCents !== undefined && (
                        <span className="tabular ml-2 opacity-70">
                          {money(v.quoteCents, v.currency ?? currency)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {(projection.data?.byCategory.length ?? 0) > 0 && (
                <ul className="flex flex-col divide-y divide-line">
                  {projection.data?.byCategory.map((row) => (
                    <li key={row.category} className="flex justify-between gap-4 py-2">
                      <span className="text-caption">{t(`vendorCategories.${row.category}`)}</span>
                      <span className="flex gap-4">
                        <span className="tabular text-caption text-leaf-soft">
                          {money(row.plannedCents, currency)}
                        </span>
                        <span className="tabular text-caption">
                          {money(row.actualCents, currency)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* A scenario has not been paid for, so no "paid" column here. */}
              <p className="text-caption text-leaf-soft">{t('budget.scenarioCaveat')}</p>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirming && selected !== undefined}
        title={t('budget.removeScenarioTitle', { name: selected?.name ?? '' })}
        body={<p>{t('budget.removeScenarioAlso')}</p>}
        confirmLabel={t('common.remove')}
        busy={remove.isPending}
        onConfirm={() => {
          if (selected) remove.mutate({ scenarioId: selected.scenarioId });
          setSelectedId(undefined);
          setConfirming(false);
        }}
        onClose={() => setConfirming(false)}
      />
    </section>
  );
}
